import React, {useEffect, useState} from 'react'
import {Card, Row, Col, Form} from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {useDispatch} from 'react-redux'
import moment from 'moment'
import {
  getOrderStatistic,
  getOrderUsageStatistic,
  selectOrderLoading,
  selectOrderStatistic,
  selectOrderUsageStatistic,
} from '../../orders/redux/OrderRedux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {convertOrderStatisticToChartData} from '../../orders/utils'
import {useIntl} from 'react-intl'
enum DATE_FILTER_RANGE {
  LAST_7_DAYS = 'last_7_days',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
}
const BranchStatistics: React.FC = () => {
  // State for bar chart filter
  const dispatch = useDispatch()
  const intl = useIntl()
  const [barFilter, setBarFilter] = useState<DATE_FILTER_RANGE>(DATE_FILTER_RANGE.LAST_7_DAYS)

  const orderStatistic = useAppSelector(selectOrderStatistic)
  const orderUsageStatistic = useAppSelector(selectOrderUsageStatistic)
  const state = {
    series: orderUsageStatistic.numberOfOrders.amount,
    options: {
      chart: {
        height: 390,
        type: 'radialBar',
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
            image: undefined,
          },
          dataLabels: {
            name: {
              show: false,
            },
            value: {
              show: false,
            },
          },
          barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: '10px',
            formatter: function (seriesName: any, opts: any) {
              return seriesName + ':  ' + opts.w.globals.series[opts.seriesIndex]
            },
          },
        },
      },
      labels: orderUsageStatistic.numberOfOrders.label,
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              show: false,
            },
          },
        },
      ],
    },
  }
  const chartData = convertOrderStatisticToChartData(orderStatistic, intl)

  const loading = useAppSelector(selectOrderLoading)
  // Dummy data for bar chart
  const barChartData = {
    series: chartData?.series ?? [],
    options: {
      chart: {
        type: 'bar',
        height: 250,
      },
      colors: ['#ff4d4f', '#1890ff', '#52c41a'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 5,
          borderRadiusApplication: 'end',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: chartData.labels,
      },
      yaxis: {
        title: {
          text: intl.formatMessage({id: 'order.total'}),
        },
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        y: {
          formatter: function (val: any) {
            return +val + ' ' + intl.formatMessage({id: 'order.total'})
          },
        },
      },
      noData: {
        text: intl.formatMessage({id: 'SYSTEM.NO_DATA'}),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          fontSize: '14px',
        },
      },
    },
  }
  const creditState = {
    series: orderUsageStatistic.numberOfCredits.amount,

    options: {
      chart: {
        type: 'donut',
      },
      labels: orderUsageStatistic.numberOfCredits.label,
      legend: {
        position: 'bottom', // <-- This moves the labels to the bottom
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 250,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
      noData: {
        text: intl.formatMessage({id: 'SYSTEM.NO_DATA'}),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          fontSize: '14px',
        },
      },
    },
  }
  useEffect(() => {
    // Fetch data for the bar chart based on the selected filter
    let endDate = moment()
    let startDate = moment()
    switch (barFilter) {
      case DATE_FILTER_RANGE.LAST_7_DAYS:
        startDate.subtract(7, 'days')
        break
      case DATE_FILTER_RANGE.THIS_MONTH:
        startDate = moment(startDate).startOf('month')
        break
      case DATE_FILTER_RANGE.LAST_MONTH:
        startDate = moment(startDate).subtract(1, 'month').startOf('month')
        endDate = moment(startDate).endOf('month')
        break
      default:
        startDate = moment(startDate).startOf('month')
        endDate = moment(startDate).endOf('month')
    }

    dispatch(
      getOrderStatistic({startDate: startDate.toISOString(), endDate: endDate.toISOString()})
    )
    dispatch(
      getOrderUsageStatistic({startDate: startDate.toISOString(), endDate: endDate.toISOString()})
    )
  }, [barFilter])
  return (
    <Card className='mb-10'>
      <Card.Body>
        <Row className='mb-5'>
          <div className='d-flex justify-content-end'>
            <Form.Select
              style={{width: 180}}
              value={barFilter}
              onChange={(e) => setBarFilter(e.target.value as any)}
            >
              {Object.values(DATE_FILTER_RANGE).map((value: string) => (
                <option key={value} value={value}>
                  {intl.formatMessage({id: `order.filter.${value}`})}
                </option>
              ))}
            </Form.Select>
          </div>
        </Row>
        <Row>
          <Col md={6} className='mb-5'>
            <div
              className='d-flex justify-content-center align-items-center mb-3'
              style={{minHeight: 38}}
            >
              <h5 className='mb-0'>{intl.formatMessage({id: 'BRANCH.ORDER_STATISTICS'})}</h5>
            </div>
            {chartData.labels.length > 0 && (
              <ReactApexChart
                options={barChartData.options as ApexOptions}
                series={barChartData.series}
                type='bar'
                loading={loading}
                height={250}
              />
            )}
          </Col>
          <Col md={3} className='mb-5'>
            <div
              className='d-flex justify-content-center align-items-center mb-3'
              style={{minHeight: 38}}
            >
              <h5 className='mb-0'>{intl.formatMessage({id: 'BRANCH.ORDER_USAGE_BY_BRANCH'})}</h5>
            </div>
            <ReactApexChart
              options={state.options as ApexOptions}
              series={state.series}
              type='radialBar'
              height={250}
            />
          </Col>
          <Col md={3} className='mb-5'>
            <div
              className='d-flex justify-content-center align-items-center mb-3'
              style={{minHeight: 38}}
            >
              <h5 className='mb-0'>{intl.formatMessage({id: 'BRANCH.CREDIT_USAGE_BY_BRANCH'})}</h5>
            </div>
            <ReactApexChart
              options={creditState.options as ApexOptions}
              series={creditState.series}
              type='donut'
              height={250}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export default BranchStatistics
